import style from './aggregated-free-cash-flow.module.scss';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { DpsDetailPanel } from '../../eval-3-workspace/dps-detail-panel';
import {
  AggregatedFreeCashFlowChart,
  AggregatedProductionVolumesChart,
  FreeCashFlowCumulativeTable,
  FreeCashFlowsBySlotTablePaginated,
} from 'components/free-cash-flow';
import {
  AggregatedFreeCashFlow as AggregatedFreeCashFlowType,
  FreeCashFlowAggregationPeriod,
  MeasurementType,
  SlotCategory,
  SlotVolumeSource,
  SlotAfeSource,
} from 'types/evaluation';
import {
  Accordion,
  AccordionItem,
  Column,
  DataTableSkeleton,
  FlexGrid as Grid,
  InlineNotification,
  Row,
} from '@carbon/react';
import classNames from 'classnames';
import {
  SLOT_CATEGORY_COLORS,
  SLOT_CATEGORY_KEYS,
  SLOT_CATEGORY_LABELS,
  slotCategoryRecord,
} from 'utils/slot-categories';
import { GlobalSettings } from './global-settings';
import { PriceDeckDetailPanel } from '../../eval-3-workspace/price-deck-detail-panel';
import { workingDcaScenarioId } from '../../eval-3-workspace/util';
import {
  selectedPriceDeckIdAtom,
  priceDeckDetailPanelOpenAtom,
  appliedConfigAtom,
  fetchPriceDecksLoadableAtom,
  selectedDcaScenarioIdAtom,
  fetchDcaScenariosLoadableAtom,
  type DpsScheduleStatus,
  type EvaluationCaseDetail,
} from '../../eval-3-workspace/atoms';
import moment from 'moment';
import { SERVER_DATE_FORMAT } from 'utils/dates';
import { PackageOverview } from './package-overview';
import { ExecutiveSummary } from './executive-summary';
import {
  RescatSlotCountChart,
  RescatSlotCountTable,
  filterRescatSlotCount,
} from 'components/rescat-slot-count';
import { CRMTracts } from '../crm-tracts';
import { SlotsContent } from '../slots-content';
import { SlotAllocationsTab } from '../slot-allocations-tab';
import {
  SlotVolumeSourceConfiguration,
  SlotAfeSourceConfiguration,
  SlotCostDateConfiguration,
  ConfigUpdater,
} from './types';
import type {
  ScheduledSlotDates,
  SlotDateOverride,
  TaxScenarioItem,
} from '../../eval-3-workspace/types';
import type { StatusItem } from 'pages/crm-tracts-subtracts/types';
import { useEvalExportPresets } from '../../eval-3-workspace/hooks';
import { useScenarioAssumptionsExport } from 'lib/hooks/useScenarioAssumptionsExport';
import { HorizontalSlotsByFormation } from '../horizontal-slots-by-formation';
import { PartiallyCoveredWells } from '../partially-covered-wells';
import { CalculationWarnings } from '../calculation-warnings';
import { PackageEvaluationsWindow } from 'types/window';

declare let window: PackageEvaluationsWindow;

const PRODUCTION_TYPES = ['oil', 'gas', 'ngl', 'water'] as const;

interface Eval2RunItem {
  id: number;
  scenario: string;
  interestType: string;
  user: string;
  runDate: string;
}

interface Props {
  evalId: number;
  packageId: number;
  scenarioHref: string;
  scenarioName?: string;
  /** The transformed result data from the calculation. */
  resultData: AggregatedFreeCashFlowType;
  /** Whether a calculation is currently in progress. */
  isLoading: boolean;
  /** The effective date setting from working copy. */
  effectiveDate: string | null;
  /** Setter for the effective date working copy atom. */
  setEffectiveDate: (date: string | null) => void;
  /** The time interval grouping setting. */
  aggregation: FreeCashFlowAggregationPeriod;
  /** Setter for the aggregation working copy atom. */
  setAggregation: (aggregation: FreeCashFlowAggregationPeriod) => void;
  /** The measurement type (net/gross) setting. */
  measurementType: MeasurementType;
  /** Setter for the measurement type working copy atom. */
  setMeasurementType: (measurementType: MeasurementType) => void;
  /** The slot volume source setting. */
  slotVolumeSource: SlotVolumeSource;
  /** Setter for the slot volume source working copy atom. */
  setSlotVolumeSource: (slotVolumeSource: SlotVolumeSource) => void;
  /** The AFE source setting. */
  slotAfeSource: SlotAfeSource;
  /** Setter for the AFE source working copy atom. */
  setSlotAfeSource: (slotAfeSource: SlotAfeSource) => void;
  /** Per-slot volume source overrides. */
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
  /** Setter for the slot volume configurations working copy atom. */
  setSlotVolumeConfigurations: ConfigUpdater<SlotVolumeSourceConfiguration>;
  /** Per-slot AFE source overrides. */
  slotAfeConfigurations: Record<string, SlotAfeSourceConfiguration>;
  /** Setter for the slot AFE configurations working copy atom. */
  setSlotAfeConfigurations: ConfigUpdater<SlotAfeSourceConfiguration>;
  /** Per-slot cost date overrides. */
  slotCostDateConfigurations: Record<string, SlotCostDateConfiguration>;
  /** Setter for the slot cost date configurations working copy atom. */
  setSlotCostDateConfigurations: ConfigUpdater<SlotCostDateConfiguration>;
  /** DPS scheduled dates keyed by slot ID (from blob fetch). */
  scheduledDates?: Record<string, ScheduledSlotDates>;
  /** Per-slot date overrides from the user. */
  slotDateConfigurations?: Record<string, SlotDateOverride>;
  /** Setter for the slot date configurations working copy atom. */
  setSlotDateConfigurations?: ConfigUpdater<SlotDateOverride>;
  /** Eval 2.0 runs (legacy, used for non-picker surfaces only). */
  eval2Runs?: Eval2RunItem[];
  /** Currently selected Eval 2.0 run ID (legacy). */
  selectedEval2Id?: number | null;
  /** Callback when a different Eval 2.0 run is selected (legacy). */
  onEval2Change?: (id: number) => void;
  /** Scenario list for the picker (INS-8827). */
  scenarios?: import('types/api-responses').ScenariosResponse[];
  /** Currently selected Scenario id (INS-8827). */
  selectedScenarioId?: number | null;
  /** Callback fired when the user picks a different Scenario (INS-8827). */
  onScenarioChange?: (id: number | null) => void;
  /** DPS list for the dropdown. */
  dpsList?: Array<{
    id: number;
    name: string;
    paceModel: string;
    referenceDate: string;
  }>;
  /** Currently selected DPS ID. */
  selectedDpsId?: number | null;
  /** Callback when a different DPS is selected. */
  onDpsChange?: (id: number) => void;
  /** DRS list for the dropdown. */
  drsList?: Array<{ id: number; name: string; effectiveDate: string | null }>;
  /** Currently selected DRS ID. */
  selectedDrsId?: number | null;
  /** Callback when a different DRS is selected. */
  onDrsChange?: (id: number | null) => void;
  /** Differential Scenario list for the dropdown. */
  differentialScenarioList?: Array<{
    id: number;
    name: string;
    effectiveDate: string | null;
  }>;
  /** Currently selected Differential Scenario ID. */
  selectedDifferentialScenarioId?: number | null;
  /** Callback when a different Differential Scenario is selected. */
  onDifferentialScenarioChange?: (id: number | null) => void;
  /** Tax Scenario list for the dropdown. */
  taxScenarioList?: TaxScenarioItem[];
  /** Currently selected Tax Scenario ID. */
  selectedTaxScenarioId?: number | null;
  /** Callback when a different Tax Scenario is selected. */
  onTaxScenarioChange?: (id: number | null) => void;
  /** Expense Scenario list for the dropdown. */
  expenseScenarioList?: Array<{
    id: number;
    name: string;
    effectiveDate: string | null;
  }>;
  /** Currently selected Expense Scenario ID. */
  selectedExpenseScenarioId?: number | null;
  /** Callback when a different Expense Scenario is selected. */
  onExpenseScenarioChange?: (id: number | null) => void;
  evalMode?: 'case' | 'legacy';
  onEvalModeChange?: (mode: 'case' | 'legacy') => void;
  evaluationCaseList?: {
    id: number;
    name: string;
    evaluationType: string;
    scope: string | null;
  }[];
  selectedEvaluationCaseId?: number | null;
  onEvaluationCaseChange?: (id: number | null) => void;
  evaluationCaseDetail?: EvaluationCaseDetail | null;
  /**
   * The DCA a completed run of the selected case ran with (its stamped one; null for Legacy), or
   * undefined when the page shows no such run.
   */
  runDcaScenarioId?: number | null;
  /** Number of slots from the DPS schedule result. */
  dpsSlotCount?: number | null;
  /** DPS scheduling status for multi-phase display. */
  dpsScheduleStatus?: DpsScheduleStatus;
  snapshotStatusItems?: StatusItem[];
  snapshotOverallStatus?: string;
  /** The calculationId (UUID) for the cashflows by month table. */
  calculationId?: string;
  /** The integer EvaluationCalculation ID for blob-cached table serving. */
  evaluationCalculationId?: number | null;
  /** Evaluation expiration date for PackageOverview. */
  evalExpiration?: string | null;
  /** Evaluation date for PackageOverview. */
  evalDate?: string | null;
  partiallyCoveredWellsHref: string;
  partiallyCoveredWellsCount: number | null;
  calculationWarnings?: string[];
  onExportAries?: () => void;
  isExportAriesDisabled?: boolean;
  isExportAriesLoading?: boolean;
  exportAriesTooltip?: string;
  // Aries MDB export. `onExportAriesMdb` opens the config modal (INS-10553);
  // run progress is shown inside the parent-owned modal.
  onExportAriesMdb?: () => void;
}

/**
 * Presentational component for the Eval 3.0 aggregated free cash flow view.
 * Renders all child sections (settings, charts, tables, slots, overview) in
 * a Grid layout. All data and callbacks come via props — no internal atom reads.
 *
 * @param {Props} props - Component props.
 * @return {JSX.Element} The aggregated free cash flow view.
 */
export const AggregatedFreeCashFlow = ({
  evalId,
  packageId,
  scenarioHref,
  scenarioName,
  resultData,
  isLoading,
  effectiveDate,
  setEffectiveDate,
  aggregation,
  setAggregation,
  measurementType,
  setMeasurementType,
  slotVolumeSource,
  setSlotVolumeSource,
  slotAfeSource,
  setSlotAfeSource,
  slotVolumeConfigurations,
  setSlotVolumeConfigurations,
  slotAfeConfigurations,
  setSlotAfeConfigurations,
  slotCostDateConfigurations,
  setSlotCostDateConfigurations,
  scheduledDates,
  slotDateConfigurations,
  setSlotDateConfigurations,
  eval2Runs,
  selectedEval2Id,
  onEval2Change,
  scenarios,
  selectedScenarioId,
  onScenarioChange,
  dpsList,
  selectedDpsId,
  onDpsChange,
  drsList,
  selectedDrsId,
  onDrsChange,
  differentialScenarioList,
  selectedDifferentialScenarioId,
  onDifferentialScenarioChange,
  taxScenarioList,
  selectedTaxScenarioId,
  onTaxScenarioChange,
  expenseScenarioList,
  selectedExpenseScenarioId,
  onExpenseScenarioChange,
  evalMode,
  onEvalModeChange,
  evaluationCaseList,
  selectedEvaluationCaseId,
  onEvaluationCaseChange,
  evaluationCaseDetail,
  runDcaScenarioId,
  dpsSlotCount,
  dpsScheduleStatus,
  snapshotStatusItems,
  snapshotOverallStatus,
  calculationId,
  evaluationCalculationId,
  evalExpiration,
  evalDate,
  partiallyCoveredWellsHref,
  partiallyCoveredWellsCount,
  calculationWarnings = [],
  onExportAries,
  isExportAriesDisabled,
  isExportAriesLoading,
  exportAriesTooltip,
  onExportAriesMdb,
}: Props) => {
  const yearLimit =
    window.CONFIG.packageEvaluations.valuationOverviewYearsLimit ?? 10;

  // Price Deck state
  const [selectedPriceDeckId, setSelectedPriceDeckId] = useAtom(
    selectedPriceDeckIdAtom(packageId)
  );
  const appliedConfig = useAtomValue(appliedConfigAtom(packageId));
  const [isPriceDeckPanelOpen, setIsPriceDeckPanelOpen] = useAtom(
    priceDeckDetailPanelOpenAtom
  );
  const [priceDecksState, triggerFetchPriceDecks] = useAtom(
    fetchPriceDecksLoadableAtom
  );
  const priceDecks =
    priceDecksState.state === 'hasData' && priceDecksState.data
      ? priceDecksState.data
      : [];

  // Fetch price decks on mount
  useEffect(() => {
    triggerFetchPriceDecks(selectedPriceDeckId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first deck only on fresh page (no calc loaded).
  // When a calc is loaded, appliedConfig is set — hydration controls the selection.
  useEffect(() => {
    if (
      priceDecksState.state === 'hasData' &&
      priceDecks.length > 0 &&
      selectedPriceDeckId === null &&
      !appliedConfig
    ) {
      setSelectedPriceDeckId(priceDecks[0].id);
    }
  }, [priceDecksState.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // DCA Scenario state
  const [selectedDcaScenarioId, setSelectedDcaScenarioId] = useAtom(
    selectedDcaScenarioIdAtom(packageId)
  );
  // A case run takes its DCA from the case: a DCA picked before the case, its picker now hidden,
  // must not drive the slot tables.
  const slotsDcaScenarioId = workingDcaScenarioId(
    {
      evalMode,
      evaluationCaseId: selectedEvaluationCaseId,
      dcaScenarioId: selectedDcaScenarioId,
    },
    evaluationCaseDetail,
    runDcaScenarioId
  );
  const [dcaScenariosState, triggerFetchDcaScenarios] = useAtom(
    fetchDcaScenariosLoadableAtom
  );
  const dcaScenarios =
    dcaScenariosState.state === 'hasData' && dcaScenariosState.data
      ? dcaScenariosState.data
      : [];
  const cashflowsColumnOrderConfigs = useEvalExportPresets('cashflows', 'csv');

  // "Export Content" — scenario-assumptions XLSX workbook for the loaded Eval 3
  // calculation. Enqueues POST /evaluations/calculations/:id/downloads/scenario_assumptions_export
  // and streams the signed download on completion via Pusher.
  const { exportStatus: contentExportStatus, startExport: startContentExport } =
    useScenarioAssumptionsExport({ calculationId: evalId });
  const handleExportContent = useCallback(() => {
    startContentExport();
  }, [startContentExport]);
  // The signed URL downloads on completion via the hook's default (same-tab `downloadFromUrl`),
  // so no consumer-side download effect is needed here.
  const isExportContentLoading = contentExportStatus.status === 'processing';
  // Disabled until a completed eval calculation is loaded (evalId === the
  // selected EvaluationCalculation id), consistent with the Export Aries gate.
  const isExportContentDisabled = !evalId;
  const exportContentTooltip = !evalId
    ? 'An Eval 3 run is required before exporting content'
    : undefined;
  // Fetch price decks and DCA scenarios on mount
  useEffect(() => {
    triggerFetchDcaScenarios();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [isDpsDetailOpen, setIsDpsDetailOpen] = useState(false);

  const [slotCategoryVisibility, setSlotCategoryVisibility] = useState<
    Record<SlotCategory, boolean>
  >(() => slotCategoryRecord(() => true));

  // Track which heavy sections have been opened (lazy mount).
  // Once opened, children stay mounted so data is not refetched on toggle.
  const [mountedSections, setMountedSections] = useState({
    partiallyCoveredWells: false,
    calculationWarnings: false,
    horizontalSlots: false,
    slots: false,
    slotAllocations: false,
  });

  const handleSectionOpen = (section: keyof typeof mountedSections) => () => {
    setMountedSections((prev) => ({ ...prev, [section]: true }));
  };

  const handleVolumeConfigChange = useCallback(
    (
      slotId: string,
      volumeSourceId: string,
      volumeSourceType: string,
      volumeSourceHref?: string,
      forecastSource?: string,
      dcaScenario?: string
    ) => {
      setSlotVolumeConfigurations((prev) => ({
        ...prev,
        [slotId]: {
          volumeSourceId,
          volumeSourceType,
          volumeSourceHref,
          forecastSource,
          dcaScenario,
        },
      }));
    },
    [setSlotVolumeConfigurations]
  );

  const handleAfeConfigChange = useCallback(
    (
      slotId: string,
      afeSourceId: string,
      afeSourceHref?: string,
      costs?: {
        drillingCost?: number;
        completionCost?: number;
        facilitiesCost?: number;
        contingenciesCost?: number;
        otherCost?: number;
      },
      afeWellName?: string,
      costDates?: SlotCostDateConfiguration
    ) => {
      setSlotAfeConfigurations((prev) => ({
        ...prev,
        [slotId]: { afeSourceId, afeSourceHref, afeWellName, ...costs },
      }));
      if (costDates) {
        setSlotCostDateConfigurations((prev) => ({
          ...prev,
          [slotId]: { ...prev[slotId], ...costDates },
        }));
      }
    },
    [setSlotAfeConfigurations, setSlotCostDateConfigurations]
  );

  const handleCostDateChange = useCallback(
    (slotId: string, dateField: string, dateValue: string) => {
      setSlotCostDateConfigurations((prev) => ({
        ...prev,
        [slotId]: { ...prev[slotId], [dateField]: dateValue },
      }));
    },
    [setSlotCostDateConfigurations]
  );

  const handleSlotDateChange = useCallback(
    (slotId: string, dateField: string, dateValue: string) => {
      setSlotDateConfigurations?.((prev) => ({
        ...prev,
        [slotId]: { ...prev[slotId], [dateField]: dateValue },
      }));
    },
    [setSlotDateConfigurations]
  );

  const productionVolumesData = useMemo(
    () => ({
      productionVolumes: resultData.productionVolumes || [],
      interval: aggregation ?? 'year',
    }),
    [resultData.productionVolumes, aggregation]
  );

  const filteredRescatSlotCount = useMemo(
    () =>
      filterRescatSlotCount(
        resultData.rescatSlotCount ?? [],
        resultData.interval
      ),
    [resultData.rescatSlotCount, resultData.interval]
  );

  const categoryHeaders = SLOT_CATEGORY_KEYS.map((category) => {
    const color = SLOT_CATEGORY_COLORS[category];
    const formattedCategory = SLOT_CATEGORY_LABELS[category];

    return {
      [category]: (
        <div>
          <button
            onClick={() =>
              setSlotCategoryVisibility((prev) => ({
                ...prev,
                [category]: !prev[category],
              }))
            }
            className={classNames(style.header, {
              [style.notVisible]: !slotCategoryVisibility[category],
            })}
          >
            <span
              className={style.circleIndicator}
              style={{ backgroundColor: color }}
            />
            <span>{`${formattedCategory} Cash Flow`}</span>
          </button>
        </div>
      ),
    };
  });

  const displayEffectiveDate =
    effectiveDate ?? moment().format(SERVER_DATE_FORMAT);

  return (
    <div className={style.container}>
      {contentExportStatus.status === 'failed' && (
        <InlineNotification
          kind="error"
          title={contentExportStatus.error ?? 'Export failed'}
          lowContrast
        />
      )}
      <Grid className={style.grid}>
        <Row>
          <GlobalSettings
            evalId={evalId}
            packageId={packageId}
            scenarioName={scenarioName}
            scenarioHref={scenarioHref}
            isLoading={isLoading}
            effectiveDate={displayEffectiveDate}
            appliedEffectiveDate={resultData.effectiveDate}
            setEffectiveDate={setEffectiveDate}
            aggregation={aggregation ?? 'year'}
            setAggregation={setAggregation}
            measurementType={measurementType}
            setMeasurementType={setMeasurementType}
            slotVolumeSource={slotVolumeSource}
            setSlotVolumeSource={setSlotVolumeSource}
            slotAfeSource={slotAfeSource}
            setSlotAfeSource={setSlotAfeSource}
            eval2Runs={eval2Runs}
            selectedEval2Id={selectedEval2Id}
            onEval2Change={onEval2Change}
            scenarios={scenarios}
            selectedScenarioId={selectedScenarioId}
            onScenarioChange={onScenarioChange}
            dpsList={dpsList}
            selectedDpsId={selectedDpsId}
            onDpsChange={onDpsChange}
            drsList={drsList}
            selectedDrsId={selectedDrsId}
            onDrsChange={onDrsChange}
            differentialScenarioList={differentialScenarioList}
            selectedDifferentialScenarioId={selectedDifferentialScenarioId}
            onDifferentialScenarioChange={onDifferentialScenarioChange}
            taxScenarioList={taxScenarioList}
            selectedTaxScenarioId={selectedTaxScenarioId}
            onTaxScenarioChange={onTaxScenarioChange}
            expenseScenarioList={expenseScenarioList}
            selectedExpenseScenarioId={selectedExpenseScenarioId}
            onExpenseScenarioChange={onExpenseScenarioChange}
            evalMode={evalMode}
            onEvalModeChange={onEvalModeChange}
            evaluationCaseList={evaluationCaseList}
            selectedEvaluationCaseId={selectedEvaluationCaseId}
            onEvaluationCaseChange={onEvaluationCaseChange}
            evaluationCaseDetail={evaluationCaseDetail}
            dpsSlotCount={dpsSlotCount}
            dpsScheduleStatus={dpsScheduleStatus}
            snapshotStatusItems={snapshotStatusItems}
            snapshotOverallStatus={snapshotOverallStatus}
            onDpsShowDetails={() => setIsDpsDetailOpen(true)}
            priceDecks={priceDecks}
            selectedPriceDeckId={selectedPriceDeckId}
            onPriceDeckChange={setSelectedPriceDeckId}
            onShowPriceDeckDetails={() => setIsPriceDeckPanelOpen(true)}
            onExportAries={onExportAries}
            isExportAriesDisabled={isExportAriesDisabled}
            isExportAriesLoading={isExportAriesLoading}
            exportAriesTooltip={exportAriesTooltip}
            onExportAriesMdb={onExportAriesMdb}
            onExportContent={handleExportContent}
            isExportContentDisabled={isExportContentDisabled}
            isExportContentLoading={isExportContentLoading}
            exportContentTooltip={exportContentTooltip}
            dcaScenarios={dcaScenarios}
            selectedDcaScenarioId={selectedDcaScenarioId}
            onDcaScenarioChange={setSelectedDcaScenarioId}
          />
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem
                title={
                  <span
                    className={
                      partiallyCoveredWellsCount
                        ? style.warningText
                        : style.mutedText
                    }
                  >
                    {`Partially Covered Wells${
                      partiallyCoveredWellsCount !== null
                        ? ` (${partiallyCoveredWellsCount})`
                        : ''
                    }`}
                  </span>
                }
                onHeadingClick={handleSectionOpen('partiallyCoveredWells')}
              >
                {mountedSections.partiallyCoveredWells && (
                  <PartiallyCoveredWells
                    href={partiallyCoveredWellsHref}
                    count={partiallyCoveredWellsCount}
                  />
                )}
              </AccordionItem>
              {calculationWarnings.length > 0 && (
                <AccordionItem
                  title={
                    <span className={style.warningText}>
                      {`Slots with Calculation Warnings (${calculationWarnings.length})`}
                    </span>
                  }
                  onHeadingClick={handleSectionOpen('calculationWarnings')}
                >
                  {mountedSections.calculationWarnings && (
                    <CalculationWarnings warnings={calculationWarnings} />
                  )}
                </AccordionItem>
              )}
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem title="Executive Summary" open>
                <ExecutiveSummary packageId={packageId} />
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem title="Package Overview" open>
                <PackageOverview
                  loading={isLoading}
                  packageOverview={resultData.packageOverview}
                  transactionEffectiveDate={resultData.effectiveDate}
                  discountToDate={resultData.effectiveDate}
                  evalExpiration={evalExpiration ?? null}
                  evalDate={evalDate ?? null}
                />
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem title="CRM Tracts" open>
                <CRMTracts
                  packageId={packageId}
                  evaluationCalculationId={evaluationCalculationId}
                />
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem
                title="Horizontal Slots by Basin/Formation"
                onHeadingClick={handleSectionOpen('horizontalSlots')}
              >
                {mountedSections.horizontalSlots && (
                  <HorizontalSlotsByFormation packageId={packageId} />
                )}
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem
                title="Slots"
                onHeadingClick={handleSectionOpen('slots')}
              >
                {mountedSections.slots && (
                  <SlotsContent
                    packageId={packageId}
                    evalId={evalId}
                    eval2EvaluationId={selectedEval2Id ?? 0}
                    slotVolumeSource={slotVolumeSource}
                    slotAfeSource={slotAfeSource}
                    dcaScenarioId={slotsDcaScenarioId}
                    calculationId={evalId}
                    dcaScenarioName={
                      dcaScenarios.find((s) => s.id === slotsDcaScenarioId)
                        ?.name
                    }
                    slotVolumeConfigurations={slotVolumeConfigurations}
                    onSlotVolumeConfigurationChange={handleVolumeConfigChange}
                    slotAfeConfigurations={slotAfeConfigurations}
                    onSlotAfeConfigurationChange={handleAfeConfigChange}
                    slotCostDateConfigurations={slotCostDateConfigurations}
                    onSlotCostDateChange={handleCostDateChange}
                    scheduledDates={scheduledDates}
                    slotDateConfigurations={slotDateConfigurations}
                    onSlotDateChange={handleSlotDateChange}
                  />
                )}
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem
                title="Slot Allocations"
                onHeadingClick={handleSectionOpen('slotAllocations')}
              >
                {mountedSections.slotAllocations && (
                  <SlotAllocationsTab
                    packageId={packageId}
                    evalId={evalId}
                    eval2EvaluationId={selectedEval2Id ?? 0}
                    slotVolumeSource={slotVolumeSource}
                    slotAfeSource={slotAfeSource}
                    dcaScenarioId={slotsDcaScenarioId}
                    calculationId={evalId}
                    slotVolumeConfigurations={slotVolumeConfigurations}
                    onSlotVolumeConfigurationChange={handleVolumeConfigChange}
                    slotAfeConfigurations={slotAfeConfigurations}
                    slotCostDateConfigurations={slotCostDateConfigurations}
                  />
                )}
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem title="Cash Flow Chart" open>
                <AggregatedFreeCashFlowChart
                  packageId={packageId}
                  measurementType={measurementType}
                  aggregatedFreeCashFlow={resultData}
                  isLoading={isLoading}
                  slotCategoryVisibility={slotCategoryVisibility}
                />
                {isLoading ? (
                  <DataTableSkeleton
                    showHeader={false}
                    showToolbar={false}
                    columnCount={6}
                    rowCount={2}
                  />
                ) : (
                  <FreeCashFlowCumulativeTable
                    measurementType={measurementType}
                    freeCashFlows={resultData.freeCashflows}
                    effectiveDate={resultData.effectiveDate}
                    noOfYears={yearLimit}
                    headerLabelContent={Object.assign({}, ...categoryHeaders, {
                      total: (
                        <div className={style.total}>
                          <label>Total</label>
                        </div>
                      ),
                    })}
                  />
                )}
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem title="Production Volumes Chart" open>
                {isLoading ? (
                  <DataTableSkeleton
                    showHeader={false}
                    showToolbar={false}
                    columnCount={6}
                    rowCount={2}
                  />
                ) : (
                  <AggregatedProductionVolumesChart
                    packageId={packageId}
                    isLoading={isLoading}
                    aggregatedProductionVolumes={productionVolumesData}
                    slotCategoryVisibility={slotCategoryVisibility}
                    measurementType={measurementType}
                    productionTypes={PRODUCTION_TYPES}
                    effectiveDate={resultData.effectiveDate}
                  />
                )}
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem title="Reserves Category Evolution" open>
                <RescatSlotCountChart
                  slotCount={filteredRescatSlotCount}
                  interval={resultData.interval}
                  isLoading={isLoading}
                />
                <RescatSlotCountTable
                  slotCount={filteredRescatSlotCount}
                  isLoading={isLoading}
                />
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
        <Row className={style.row}>
          <Column>
            <Accordion className={style.accordion}>
              <AccordionItem title="Cashflows by Month Table" open>
                {isLoading || !resultData.freeCashflows?.length ? (
                  <DataTableSkeleton
                    showHeader={false}
                    showToolbar={false}
                    columnCount={4}
                    rowCount={5}
                  />
                ) : (
                  <FreeCashFlowsBySlotTablePaginated
                    calculationId={calculationId}
                    evaluationId={evalId}
                    effectiveDate={effectiveDate ?? undefined}
                    exportPresets={cashflowsColumnOrderConfigs}
                  />
                )}
              </AccordionItem>
            </Accordion>
          </Column>
        </Row>
      </Grid>
      <PriceDeckDetailPanel
        packageId={packageId}
        priceDeckId={selectedPriceDeckId}
        open={isPriceDeckPanelOpen}
        onClose={() => setIsPriceDeckPanelOpen(false)}
      />

      <DpsDetailPanel
        dpsId={selectedDpsId ?? null}
        open={isDpsDetailOpen}
        onClose={() => setIsDpsDetailOpen(false)}
        evaluationCalculationId={evaluationCalculationId}
      />
    </div>
  );
};
